import React, {useCallback, useMemo, useState} from 'react';
import {Alert, Descriptions, Space, Tag, Typography} from 'antd';
import PromptItem from './PromptItem';
import {
    OriginalAction,
    PromptOverview,
    TrackCloseExecutionSummary,
    TrackEvolutionEvent,
    TrackRunEvolution,
} from './trackEvolutionTypes';
import {botService, PromptHistoryResponse} from '../../services/botService';

const {Text, Paragraph} = Typography;

interface Props {
    event: TrackEvolutionEvent;
    originalAction?: OriginalAction | null;
    selected?: boolean;
    forecastColor?: string;
    hasInitialForecast?: boolean;
    onSelect?: () => void;
}

const EXECUTION_LABELS: Record<string, string> = {
    OBSERVE: '仅观察',
    EXECUTE: '执行交易',
};
const TREND_LABELS: Record<string, string> = {
    MATCHED: '符合预期',
    DEVIATED: '偏离预期',
    INCONCLUSIVE: '暂无法判断',
};
const OUTCOME_LABELS: Record<string, string> = {
    KEEP: '继续持有',
    PARTIAL_CLOSE: '部分平仓',
    FULL_CLOSE: '全部平仓',
    SUCCESS: '执行成功',
    FAILED: '执行失败',
};

function formatTs(value: number | null | undefined): string {
    return value == null ? '-' : new Date(value).toLocaleString();
}

function formatPrice(value: number | null | undefined): string {
    return value == null ? '-' : value.toLocaleString('en-US', {maximumFractionDigits: 2});
}

export function runStatusColor(status: string): string {
    if (status === 'COMPLETED' || status === 'GAP_REFRESHED') return 'success';
    if (status.includes('FAILED')) return 'error';
    if (status.startsWith('SKIPPED') || status === 'DATA_NOT_READY') return 'warning';
    if (status === 'RUNNING' || status === 'ORDER_PENDING') return 'processing';
    return 'default';
}

const RUN_FAILURE_STATUSES = new Set(['FAILED', 'MODEL_FAILED', 'VALIDATION_FAILED']);

/**
 * errorMessage 只作为失败状态的详情展示。
 * 兼容旧数据中 ORDER_PENDING/COMPLETED Run 被错误写入“下单成功”的情况。
 */
export function hasRunExecutionError(
    run: Pick<TrackRunEvolution, 'status' | 'errorCode' | 'errorMessage'>,
): boolean {
    return RUN_FAILURE_STATUSES.has(run.status) && Boolean(run.errorCode || run.errorMessage);
}

function overviewItem(
    decision: PromptOverview,
    action: OriginalAction | null,
): PromptHistoryResponse {
    return {
        decisionId: decision.decisionId,
        apiKeyId: decision.apiKeyId,
        modelName: decision.modelName,
        createdTime: decision.createdTime,
        action: decision.action || action?.actionType || '',
        instId: decision.instId || action?.instId || '',
        price: decision.price ?? action?.price ?? null,
        quantity: decision.quantity ?? action?.quantity ?? null,
        takeProfit: action?.takeProfit ?? null,
        stopLoss: action?.stopLoss ?? null,
        confidence: decision.confidence,
        reasoning: '',
        timeframe: action?.timeframe || '',
        posSide: action?.posSide ?? null,
        openCloses: action?.openClose ?? null,
        promptContent: '',
        responseSegments: [],
        status: decision.status,
        errorMessage: decision.errorMessage,
        executed: decision.executed,
        executionTime: null,
        executionResult: null,
        processingTimeMs: decision.processingTimeMs,
        inputTotalEquity: null,
        inputAvailableBalance: null,
        inputUsedMargin: null,
        inputUnrealizedPnl: null,
        inputMarginRatio: null,
        callSource: decision.callSource,
        trackUnitId: decision.trackUnitId,
        trackRunId: decision.trackRunId,
    };
}

const CloseExecution: React.FC<{ value: TrackCloseExecutionSummary }> = ({value}) => (
    <div className="track-evolution-close-exec">
        <Space wrap>
            <Text strong>平仓执行</Text>
            <Tag>{value.statusLabel}</Tag>
            {value.failureMessage && <Tag color="error">执行异常</Tag>}
        </Space>
        <Descriptions size="small" column={{xs: 1, sm: 2, md: 4}} colon={false}>
            <Descriptions.Item label="请求量">{value.requestedQty ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="提交量">{value.submittedQty ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="成交量">{value.filledQty ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="成交均价">{value.averageFillPrice ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="订单 ID">{value.orderId ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="提交时间">{formatTs(value.submittedAtMs)}</Descriptions.Item>
            <Descriptions.Item label="完成时间">{formatTs(value.completedAtMs)}</Descriptions.Item>
        </Descriptions>
        {value.failureMessage && <Alert type="error" showIcon message={value.failureMessage}/>}
    </div>
);

const RunSummary: React.FC<{ run: TrackRunEvolution }> = ({run}) => (
    <div className="track-evolution-run-summary">
        <Space wrap>
            <Text strong>Track Run #{run.runId}</Text>
            <Tag color={runStatusColor(run.status)}>{run.statusLabel}</Tag>
            <Tag>{EXECUTION_LABELS[run.executionMode] || run.executionMode || '-'}</Tag>
            {run.outputVersionNo == null
                ? <Tag>本轮未生成新预测</Tag>
                : <Tag color="cyan">生成 V{run.outputVersionNo}</Tag>}
        </Space>
        <Descriptions size="small" column={{xs: 1, sm: 2, md: 4}} colon={false}>
            <Descriptions.Item label="输入预测">
                {run.inputVersionNo == null ? '-' : `V${run.inputVersionNo}`}
            </Descriptions.Item>
            <Descriptions.Item label="预期状态">
                {run.expectedTrendStatus ? TREND_LABELS[run.expectedTrendStatus] || run.expectedTrendStatus : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Track 结果">
                {run.trackOutcome ? OUTCOME_LABELS[run.trackOutcome] || run.trackOutcome : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="置信度">
                {run.confidence == null ? '-' : `${run.confidence}%`}
            </Descriptions.Item>
            <Descriptions.Item label="比较开始">{formatTs(run.comparisonStartTimeMs)}</Descriptions.Item>
            <Descriptions.Item label="比较结束">{formatTs(run.comparisonEndTimeMs)}</Descriptions.Item>
            <Descriptions.Item label="请求/允许/规范化数量" span={2}>
                {`${run.requestedQty ?? '-'} / ${run.allowedQty ?? '-'} / ${run.normalizedQty ?? '-'}`}
            </Descriptions.Item>
        </Descriptions>
        <div className="track-evolution-reasoning">
            <Text strong>决策原因</Text>
            <Paragraph>{run.reasoning || '未提供规范化 reasoning'}</Paragraph>
        </div>
        {run.closeExecution && <CloseExecution value={run.closeExecution}/>}
        {hasRunExecutionError(run) && (
            <Alert type="error" showIcon message={run.errorCode || 'Track 执行异常'} description={run.errorMessage}/>
        )}
        {run.validationErrors?.length > 0 && (
            <Alert type="warning" showIcon message="结果校验未通过" description={run.validationErrors.join('；')}/>
        )}
    </div>
);

const OriginalSummary: React.FC<{
    action: OriginalAction | null;
    hasInitialForecast: boolean;
}> = ({action, hasInitialForecast}) => (
    <div className="track-evolution-run-summary original">
        <Space wrap>
            <Text strong>原始决策</Text>
            {hasInitialForecast && <Tag color="cyan">V1 · 初始预测</Tag>}
            <Tag color="gold">创建当前 Track Unit</Tag>
        </Space>
        {action && (
            <Descriptions size="small" column={{xs: 1, sm: 2, md: 3}} colon={false}>
                <Descriptions.Item label="方向">{action.posSide ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="开平仓">{action.openClose ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="价格">{formatPrice(action.price)}</Descriptions.Item>
                {action.quantity != null && (
                    <Descriptions.Item label="数量">{action.quantity}</Descriptions.Item>
                )}
                <Descriptions.Item label="止盈">{formatPrice(action.takeProfit)}</Descriptions.Item>
                <Descriptions.Item label="止损">{formatPrice(action.stopLoss)}</Descriptions.Item>
                {action.timeframe != null && (
                    <Descriptions.Item label="周期">{action.timeframe}</Descriptions.Item>
                )}
            </Descriptions>
        )}
    </div>
);

const TrackEvolutionEventCard: React.FC<Props> = ({
                                                      event,
                                                      originalAction,
                                                      selected,
                                                      forecastColor,
                                                      hasInitialForecast = false,
                                                      onSelect,
                                                  }) => {
    const [detail, setDetail] = useState<PromptHistoryResponse | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const decision = event.decision;
    const item = useMemo(
        () => detail || (decision ? overviewItem(
            decision,
            event.eventType === 'ORIGINAL_DECISION' ? originalAction ?? null : null,
        ) : null),
        [detail, decision, event.eventType, originalAction],
    );

    const loadDetail = useCallback(async () => {
        if (!decision || detail || detailLoading) return;
        setDetailLoading(true);
        setDetailError(null);
        try {
            const response = await botService.getPromptDetail(decision.decisionId);
            if (!response.success || !response.data) {
                throw new Error(response.message || '详情加载失败');
            }
            setDetail(response.data);
        } catch (error: any) {
            setDetailError(error.message || '详情加载失败');
        } finally {
            setDetailLoading(false);
        }
    }, [decision, detail, detailLoading]);

    return (
        <article
            className={`track-evolution-event-card${selected ? ' selected' : ''}`}
            style={forecastColor ? {borderLeftColor: forecastColor} : undefined}
            onClick={onSelect}
        >
            {event.eventType === 'ORIGINAL_DECISION'
                ? <OriginalSummary
                    action={originalAction ?? null}
                    hasInitialForecast={hasInitialForecast}
                />
                : event.run
                    ? <RunSummary run={event.run}/>
                    : <Alert type="warning" showIcon message="Track Run 数据不可用"/>}

            {detailError && (
                <Alert
                    type="error"
                    showIcon
                    message="Prompt 详情加载失败"
                    description={detailError}
                />
            )}
            {item ? (
                <div className={detailLoading ? 'track-evolution-prompt-loading' : undefined}>
                    <PromptItem
                        item={item}
                        onExpandedStateChange={(_key, expanded) => {
                            if (expanded) void loadDetail();
                        }}
                        readOnly
                    />
                </div>
            ) : event.eventType === 'ORIGINAL_DECISION' ? (
                <Alert type="warning" showIcon message="原始决策数据不可用"/>
            ) : null}
        </article>
    );
};

export default TrackEvolutionEventCard;
